import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {BehaviorSubject, Observable, of, throwError} from 'rxjs';
import {filter, map, switchMap, take, tap} from 'rxjs/operators';
import {Utils} from '../../../shared/utils';
import {GeneralPagination} from '../../../shared/shared.types';
import {Contact, ExternalContractor} from './external-contractor.types';
import {WorkOrder} from "../../omng/work-orders/work-orders.types";
import {cloneDeep} from "lodash";
import {ToastrService} from "ngx-toastr";
import {TranslocoService} from "@ngneat/transloco";
import {Router} from "@angular/router";

@Injectable({
    providedIn: 'root',
})
export class ExternalContractorsService {
    private _customer: BehaviorSubject<ExternalContractor | null> = new BehaviorSubject<ExternalContractor | null>(null)
    private _contacts: BehaviorSubject<Contact[] | null> = new BehaviorSubject<Contact[] | null>(null);
    private _contact: BehaviorSubject<Contact | null> = new BehaviorSubject<Contact>(null);

    private customerTypesArray = [
        {
            id: this._utils.CUSTOMER_TYPE_COMPANY,
            translate: 'CUSTOMERS.TYPES.COMPANY',
        },
        {
            id: this._utils.CUSTOMER_TYPE_PERSON,
            translate: 'CUSTOMERS.TYPES.NATURAL_PERSON'
        }
    ]

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient,
                private toastr: ToastrService,
                private _router: Router,
                private transloco: TranslocoService,
                private _utils: Utils) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get customer types
     */
    get customerTypes(): any[] {
        return this.customerTypesArray;
    }

    /**
     * Getter for externalContractor
     */
    get customer$(): Observable<ExternalContractor> {
        return this._customer.asObservable();
    }

    /**
     * Getter for customer
     *
     */
    get customer() {
        return this._customer.value;
    }

    /**
     * Setter for customer
     *
     * @param value
     */
    set customer(value: ExternalContractor | null) {
        this._customer.next(value)
    }

    /**
     * Getter for customer contact
     */
    get contact$(): Observable<any> {
        return this._contact.asObservable();
    }

    /**
     * Getter for customer contacts
     */
    get contacts$(): Observable<Contact[]> {
        return this._contacts.asObservable();
    }

    /**
     * Setter for customer contacts
     *
     * @param contacts
     */
    set contacts(contacts: Contact[]) {
        this._contacts.next(contacts)
    }

    /**
     * Setter for contact
     *
     * @param contact
     */
    set contact(contact: Contact | any) {
        this._contact.next(contact)
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get companies
     *
     *
     * @param page
     * @param size
     * @param sort
     * @param order
     * @param search
     */
    getCustomers(page: number = 0, size: number = 10, sort: string = 'name', order: 'asc' | 'desc' | '' = 'asc', search: string = ''):
        Observable<{ pagination: GeneralPagination; data: ExternalContractor[] }> {
        return this._httpClient.get<{
            pagination: GeneralPagination;
            data: ExternalContractor[]
        }>(this._utils.BASE_URL + 'external-contractors', {
            params: {
                page: '' + page,
                size: '' + size,
                sort,
                order,
                search,
            },
        });
    }

    /**
     * Get all ext contractors
     *
     */
    getAllExternalContractors(): Observable<{ status: boolean; data: ExternalContractor[] }> {
        return this._httpClient.get<{
            status: boolean;
            data: ExternalContractor[]
        }>(this._utils.BASE_URL + 'external-contractors', {
            params: {
                use_pagination: 0,
            },
        });
    }

    /**
     * Get customer by id
     */
    getCustomerById(id: number): Observable<any> {
        return this._httpClient.get<any>(this._utils.BASE_URL + 'external-contractors/' + id)
            .pipe(
                map(e => ({
                    ...e.data as ExternalContractor,
                })),
                tap(customer => {
                    this.customer = customer;
                })
            );
    }

    /**
     * Create customer
     */
    createCustomer(customer: ExternalContractor): Observable<{ status: boolean, external_contractor_id: number }> {
        return this._httpClient.post<any>(this._utils.BASE_URL + 'external-contractors', customer);
    }

    /**
     * Update customer
     *
     * @param customer
     */
    updateCustomer(customer: ExternalContractor): Observable<{ status: boolean, data: ExternalContractor }> {
        return this._httpClient.put<any>(this._utils.BASE_URL + 'external-contractors', customer);
    }

    /**
     * Delete the customer
     *
     * @param id
     */
    deleteCustomer(id: number): Observable<any> {
        return this._httpClient.delete<any>(this._utils.BASE_URL + 'external-contractors/' + id);
    }

    /** CUSTOMER CONTACTS **/

    /**
     * Delete the contact
     *
     * @param customer_id
     * @param contact_id
     */
    deleteContact(customer_id: number, contact_id?: string): Observable<boolean> {
        console.log("Contact id to delete: ", contact_id)
        if (contact_id === 'new') {
            // Find the index of the deleted contact
            const index = this._contacts.value.findIndex(item => item.id === contact_id);

            console.log(index);

            if (index != -1) {

                // Delete the contact
                let contacts = cloneDeep(this._contacts).value
                contacts.splice(index, 1);

                // Update the contacts
                this._contacts.next(contacts);

            }

            // Return the deleted status
            return of(true);
        } else {
            return this.contacts$.pipe(
                take(1),
                switchMap(contacts => this._httpClient.post<any>(this._utils.BASE_URL + `customers/contacts/delete`, {contact_id: contact_id}).pipe(
                    map((response) => {

                        // Find the index of the deleted contact
                        const index = this._contacts.value.findIndex(item => item.id === contact_id);

                        // Delete the contact
                        let contacts = cloneDeep(this._contacts).value
                        contacts.splice(index, 1);

                        this.toastr.success('', this.transloco.translate('SUCCESS'));

                        // Update the contacts
                        this._contacts.next(contacts);

                        // Return the deleted status
                        return true;
                    })
                ))
            );
        }
    }

    /**
     * Get customer contact
     *
     * @param customer_id
     * @param search
     */
    get_contacts(customer_id: number, search?: string): Observable<{
        status: boolean,
        data: any[]
    }> {
        return this._httpClient.post<{
            status: boolean;
            data: any[]
        }>(this._utils.BASE_URL + 'customers/contacts/' + customer_id,
            {
                search: search
            })
            .pipe(
                tap((data: any) => {
                    // this._utils.progress.next(true)
                    console.log("Get contacts req: ", data)
                    this.contacts = data.data;
                })
            );
    }

    /**
     * Get contact by id
     *
     * @param contact_id
     * @param customer_id
     */
    get_contact_by_id(contact_id: string | number, customer_id: number) {
        console.log("Contact id: ", contact_id)
        if (contact_id !== 'new') {
            return this._httpClient.get<any>(this._utils.BASE_URL + 'customers/contacts/' + contact_id).pipe(
                tap((response) => {
                    if (response.status) {
                        let data = response.data;
                        this.contact = data;
                    } else {
                        this.contact = {};
                    }
                    this._utils.progress.next(false)
                })
            );
        } else {
            return this._contacts.pipe(
                take(1),
                map((contacts) => {

                    // Find the contact
                    const contact = contacts.find(item => item.id === contact_id) || null;

                    // Update the contact
                    this.contact = contact;

                    if (!contact) {
                        this._router.navigateByUrl('/core/customers/' + customer_id + '/contacts')
                        return throwError('Could not found contact with id of ' + contact_id + '!');
                    }

                    // Return the contact
                    return contact;
                }),
                switchMap((contact) => {
                    this._utils.progress.next(false)

                    if (!contact) {
                        this._router.navigateByUrl('/core/customers/' + customer_id + '/contacts')
                        return throwError('Could not found contact with id of ' + contact_id + '!');
                    }

                    return of(contact);
                })
            );
        }
    }

    /**
     * Add contact for specific customer
     */
    add_contact(): Observable<any> {
        return this.contacts$.pipe(
            take(1),
            switchMap(contacts => {

                let contact: any = {};
                contact.id = 'new'

                console.log("Contact: ", contact)

                // Update the contacts with the new contact
                this._contacts.next([contact, ...contacts]);

                // Return the new contact
                return of(contact);

            })
        );
    }

    /**
     * Update contact
     *
     * @param customer_id
     * @param contact
     */
    update_contact(customer_id: number, contact: any): Observable<any> {
        return this.contacts$.pipe(
            take(1),
            switchMap(contacts => this._httpClient.post<any>(this._utils.BASE_URL + 'customers/contacts/update', {
                ...contact
            }).pipe(
                map((response) => {

                    if (response.status) {
                        let updatedContact = response.data;

                        // Find the index of the updated contact
                        const index = contacts.findIndex(item => +item.id === +updatedContact.id);

                        console.log("contacts: ", contacts)
                        console.log("updatedContact: ", updatedContact)
                        console.log("INDEX: ", index)

                        // Update the contact
                        contacts[index] = updatedContact;

                        // Update the contacts
                        this._contacts.next([...contacts]);

                        // Return the updated contact
                        return updatedContact;

                    } else {
                    }

                }),
                switchMap(
                    updatedContact => this.contact$.pipe(
                        take(1),
                        filter(item => item && item.id === updatedContact.id),
                        tap(() => {

                            if (updatedContact) {
                                // Update the contact if it's selected
                                this.contact = updatedContact;

                                // Return the updated contact
                                return updatedContact;
                            } else {
                                return updatedContact;
                            }
                        })
                    ))
            ))
        );
    }

    /**
     * Add new contact to the customer
     *
     * @param customer_id
     * @param contact
     */
    add_new_contact(customer_id: number, contact: any) {

        return this.contacts$.pipe(
            take(1),
            switchMap(contacts => this._httpClient.post<any>(this._utils.BASE_URL + 'customers/contacts/add', {
                ...contact,
                customer_id: customer_id
            }).pipe(
                map((response) => {

                    if (response.status) {

                        let updatedContact = response.data;

                        // Find the index of the updated contact
                        const index = contacts.findIndex(item => item.id === 'new');

                        // Update the contact
                        contacts[index] = updatedContact;

                        // Update the contacts
                        this._contacts.next([...contacts]);

                        // Return the updated contact
                        return updatedContact;

                    } else {
                        return false;
                    }
                }),
                switchMap(newContact => this.contacts$.pipe(
                    take(1),
                    tap(() => {

                        if (newContact) {

                            // Update the contact if it's selected
                            this.contact = newContact;

                            // Return the updated contact
                            return newContact;
                        } else {
                            return false;
                        }
                    })
                ))
            ))
        );
    }

}
